import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Upload, Ban } from "lucide-react";
import {
    listExpenses, createExpense, voidExpense, bulkImportExpenses,
    EXPENSE_CATEGORIES, PAY_METHODS, EXPENSE_STATUSES, CATEGORY_LABEL, todayIso,
} from "@/lib/finance";
import { formatInr } from "@/lib/format";
import { errorMessage } from "@/lib/api";

export default function ExpensesPage() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ category: "", status: "", start_date: "", end_date: "", vendor: "" });
    const [createOpen, setCreateOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await listExpenses({ ...filters, limit: 500 });
            setRows(r.expenses || []); setTotal(r.total || 0);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [filters.category, filters.status, filters.start_date, filters.end_date, filters.vendor]); // eslint-disable-line

    const filteredTotal = useMemo(() => rows.reduce((a, r) => a + (r.amount_paise || 0), 0), [rows]);

    return (
        <div data-testid="expenses-page">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-orange-600">Finance · Expenses</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900">Expense Ledger</h1>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="exp-bulk-btn"><Upload size={14} className="mr-1" /> Bulk Import</Button>
                    <Button className="nest-gradient text-white" onClick={() => setCreateOpen(true)} data-testid="exp-create-btn"><Plus size={14} className="mr-1" /> Record Expense</Button>
                </div>
            </div>

            <Card className="mb-3 rounded-2xl border border-neutral-200 p-3">
                <div className="flex flex-wrap gap-2">
                    <FilterSelect value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} placeholder="All categories"
                        items={EXPENSE_CATEGORIES.map((c) => ({ value: c.value, label: `${c.emoji} ${c.label}` }))} testid="filter-category" />
                    <FilterSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} placeholder="All statuses"
                        items={EXPENSE_STATUSES.map((s) => ({ value: s, label: s }))} testid="filter-status" />
                    <Input type="date" placeholder="From" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="w-40" data-testid="filter-start" />
                    <Input type="date" placeholder="To" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="w-40" data-testid="filter-end" />
                    <Input placeholder="Vendor…" value={filters.vendor} onChange={(e) => setFilters({ ...filters, vendor: e.target.value })} className="w-40" data-testid="filter-vendor" />
                </div>
            </Card>

            <Card className="mb-3 rounded-2xl border border-orange-200 bg-orange-50 p-3 text-sm font-semibold text-orange-900" data-testid="exp-filtered-total">
                Filtered total: {formatInr(filteredTotal)} · {rows.length} rows (of {total})
            </Card>

            <Card className="rounded-2xl border border-neutral-200">
                {loading ? <div className="p-4 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
                    : rows.length === 0 ? <div className="p-10 text-center text-sm text-neutral-500">No expenses match your filters.</div>
                    : <Table>
                        <TableHeader><TableRow>
                            <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead>
                            <TableHead>Vendor</TableHead><TableHead>Amount</TableHead><TableHead>Method</TableHead>
                            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>{rows.map((r) => (
                            <TableRow key={r.id} data-testid={`exp-row-${r.id}`}>
                                <TableCell className="text-xs">{r.expense_date}</TableCell>
                                <TableCell><Badge variant="outline" className="text-[10px]">{CATEGORY_LABEL[r.category] || r.category}</Badge></TableCell>
                                <TableCell className="max-w-[220px] truncate">{r.description}</TableCell>
                                <TableCell className="text-xs">{r.vendor || "—"}</TableCell>
                                <TableCell className="font-mono">{formatInr(r.amount_paise)}</TableCell>
                                <TableCell className="text-xs">{r.payment_method || "—"}</TableCell>
                                <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${r.status === "COMPLETED" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : r.status === "VOID" ? "border-neutral-300 bg-neutral-100 text-neutral-500" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{r.status}</Badge></TableCell>
                                <TableCell className="text-right">
                                    {r.status !== "VOID" && <Button size="icon" variant="ghost" className="text-rose-600" title="Void"
                                        onClick={async () => { if (!window.confirm("Void this expense?")) return; try { await voidExpense(r.id); toast.success("Voided"); load(); } catch (e) { toast.error(errorMessage(e)); } }}
                                        data-testid={`exp-void-${r.id}`}><Ban size={14} /></Button>}
                                </TableCell>
                            </TableRow>
                        ))}</TableBody>
                    </Table>
                }
            </Card>

            <CreateDialog open={createOpen} onOpenChange={setCreateOpen} onDone={() => { setCreateOpen(false); load(); }} />
            <BulkDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={() => { setBulkOpen(false); load(); }} />
        </div>
    );
}

function FilterSelect({ value, onChange, placeholder, items, testid }) {
    return (
        <Select value={value || "__any__"} onValueChange={(v) => onChange(v === "__any__" ? "" : v)}>
            <SelectTrigger className="w-44 text-xs" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
            <SelectContent>
                <SelectItem value="__any__">{placeholder}</SelectItem>
                {items.map((it) => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}

function CreateDialog({ open, onOpenChange, onDone }) {
    const [form, setForm] = useState({ category: "SALARIES", amount_rupees: "", expense_date: todayIso(), description: "", vendor: "", payment_method: "BANK_TRANSFER", payment_reference: "", status: "COMPLETED" });
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setForm({ category: "SALARIES", amount_rupees: "", expense_date: todayIso(), description: "", vendor: "", payment_method: "BANK_TRANSFER", payment_reference: "", status: "COMPLETED" }); }, [open]);
    const submit = async () => {
        const amt = Math.round(Number(form.amount_rupees) * 100);
        if (!amt || amt <= 0) return toast.error("Amount required");
        if (!form.description.trim()) return toast.error("Description required");
        try {
            setBusy(true);
            await createExpense({
                category: form.category, amount_paise: amt, expense_date: form.expense_date,
                description: form.description.trim(), vendor: form.vendor || null,
                payment_method: form.payment_method, payment_reference: form.payment_reference || null,
                status: form.status,
            });
            toast.success("Expense recorded"); onDone?.();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="exp-create-dialog">
                <DialogHeader><DialogTitle>Record Expense</DialogTitle><DialogDescription>New HO expense entry.</DialogDescription></DialogHeader>
                <div className="grid gap-3 md:grid-cols-2">
                    <div><Label>Category *</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                            <SelectTrigger data-testid="exp-cat"><SelectValue /></SelectTrigger>
                            <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}</SelectContent>
                        </Select></div>
                    <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount_rupees} onChange={(e) => setForm({ ...form, amount_rupees: e.target.value })} data-testid="exp-amount" /></div>
                    <div><Label>Date *</Label><Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="exp-date" /></div>
                    <div><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="exp-vendor" /></div>
                    <div className="md:col-span-2"><Label>Description *</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="exp-desc" /></div>
                    <div><Label>Payment method</Label>
                        <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select></div>
                    <div><Label>Reference</Label><Input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="nest-gradient text-white" data-testid="exp-submit">{busy ? "Saving…" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkDialog({ open, onOpenChange, onDone }) {
    const [text, setText] = useState('{\n  "rows": [\n    {"category":"UTILITIES","amount_paise":50000,"expense_date":"2026-08-01","description":"Water bill"}\n  ]\n}');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        try {
            setBusy(true); setResult(null);
            const parsed = JSON.parse(text);
            const rows = Array.isArray(parsed) ? parsed : parsed.rows || [];
            const res = await bulkImportExpenses(rows);
            setResult(res);
            toast.success(`Created ${res.created_count} · Errors ${res.errors.length}`);
            if (res.created_count > 0) onDone?.();
        } catch (e) { toast.error(errorMessage(e) || "Invalid JSON"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="exp-bulk-dialog">
                <DialogHeader><DialogTitle>Bulk Import Expenses</DialogTitle><DialogDescription>Paste JSON `{`{rows: [...]}`}` — sample below.</DialogDescription></DialogHeader>
                <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-xs" data-testid="exp-bulk-text" />
                {result && (
                    <div className="mt-2 rounded-lg border p-2 text-xs" data-testid="exp-bulk-result">
                        <div><b>Created:</b> {result.created_count}</div>
                        {result.errors.length > 0 && (
                            <details className="mt-1"><summary className="cursor-pointer font-semibold text-rose-700">{result.errors.length} errors</summary>
                                <pre className="mt-1 whitespace-pre-wrap">{JSON.stringify(result.errors, null, 2)}</pre>
                            </details>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                    <Button onClick={submit} disabled={busy} className="nest-gradient text-white" data-testid="exp-bulk-submit">{busy ? "Importing…" : "Import"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
